import { useEffect, useState, useMemo } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Plus, PencilSimple, Trash, Target, CheckCircle, Clock, Spinner, Palette, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Semaforo } from "../components/Semaforo";
import { formatDate } from "../lib/format";
import { useConfirm } from "../components/ConfirmDialog";

const ESTADOS = [
  { v: "planificacion", label: "Planificación" },
  { v: "en_ejecucion", label: "En ejecución" },
  { v: "lograda", label: "Lograda" },
  { v: "no_lograda", label: "No lograda" },
  { v: "postergada", label: "Postergada" },
];

const PROGRESS_MODES = [
  { v: "manual", label: "Manual — el responsable ingresa el avance" },
  { v: "auto", label: "Automático — calcula desde proyectos/compromisos vinculados" },
  { v: "hybrid", label: "Híbrido — manual + auto (toma el mayor)" },
];

const AUTO_SOURCES = [
  { v: "projects", label: "Proyectos vinculados" },
  { v: "commitments", label: "Compromisos vinculados" },
];

const AUTO_METRICS_PROJECTS = [
  { v: "count", label: "Cantidad de proyectos" },
  { v: "beneficiarios_directos", label: "Suma de beneficiarios directos" },
  { v: "beneficiarios_indirectos", label: "Suma de beneficiarios indirectos" },
  { v: "inversion", label: "Suma de inversión ejecutada" },
];

const AUTO_METRICS_COMMITMENTS = [
  { v: "count", label: "Cantidad de compromisos" },
  { v: "cumplidos", label: "Compromisos cumplidos" },
  { v: "inversion", label: "Suma de inversión" },
];

const COLORS_PRESET = ["#0ea5e9", "#f59e0b", "#10b981", "#a855f7", "#ef4444", "#22c55e", "#6366f1", "#d97706", "#ec4899", "#14b8a6", "#0891b2", "#7c2d12"];

function emptyForm() {
  const y = new Date().getFullYear();
  return {
    name: "", description: "", theme_id: "",
    period_year_start: y, period_year_end: y,
    baseline: 0, target_value: 100, unit: "unidades",
    current_value: 0, progress_mode: "manual",
    auto_source: null, auto_metric: null,
    responsable: "", estado: "planificacion", notas: "",
    milestones: [], links: [],
  };
}

export default function Goals() {
  const confirm = useConfirm();
  const [tab, setTab] = useState("dashboard");
  const [year, setYear] = useState(new Date().getFullYear());
  const [themes, setThemes] = useState([]);
  const [goals, setGoals] = useState([]);
  const [dash, setDash] = useState(null);
  const [filterTheme, setFilterTheme] = useState("");
  const [filterEstado, setFilterEstado] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [openThemes, setOpenThemes] = useState(false);
  const [newTheme, setNewTheme] = useState({ name: "", color: "#0ea5e9" });

  const loadThemes = async () => {
    const { data } = await api.get("/goal-themes");
    setThemes(data);
  };
  const loadGoals = async () => {
    const params = { year };
    if (filterTheme) params.theme_id = filterTheme;
    if (filterEstado) params.estado = filterEstado;
    const { data } = await api.get("/goals", { params });
    setGoals(data);
  };
  const loadDash = async () => {
    const { data } = await api.get("/goals/dashboard", { params: { year } });
    setDash(data);
  };
  useEffect(() => { loadThemes(); }, []);
  useEffect(() => { loadGoals(); loadDash(); /* eslint-disable-next-line */ }, [year, filterTheme, filterEstado]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setOpen(true);
  };

  const openEdit = async (g) => {
    const { data } = await api.get(`/goals/${g.id}`);
    setEditingId(g.id);
    setForm({
      name: data.goal.name,
      description: data.goal.description || "",
      theme_id: data.goal.theme_id || "",
      period_year_start: data.goal.period_year_start,
      period_year_end: data.goal.period_year_end,
      baseline: Number(data.goal.baseline || 0),
      target_value: Number(data.goal.target_value),
      unit: data.goal.unit,
      current_value: Number(data.goal.current_value || 0),
      progress_mode: data.goal.progress_mode,
      auto_source: data.goal.auto_source,
      auto_metric: data.goal.auto_metric,
      responsable: data.goal.responsable || "",
      estado: data.goal.estado,
      notas: data.goal.notas || "",
      milestones: data.milestones.map((m) => ({
        label: m.label, target_date: m.target_date, target_value: m.target_value,
        achieved: m.achieved, order_index: m.order_index,
      })),
      links: data.links.map((l) => ({ entity_type: l.entity_type, entity_id: l.entity_id })),
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name || !form.target_value || !form.theme_id) {
      toast.error("Nombre, eje y valor objetivo son obligatorios");
      return;
    }
    try {
      if (editingId) {
        await api.put(`/goals/${editingId}`, form);
        toast.success("Meta actualizada");
      } else {
        await api.post("/goals", form);
        toast.success("Meta creada");
      }
      setOpen(false);
      loadGoals();
      loadDash();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    }
  };

  const remove = async (g) => {
    if (!(await confirm({ title: "Eliminar meta", description: `¿Deseas eliminar la meta "${g.name}"? Esta acción no se puede deshacer.`, danger: true }))) return;
    try {
      await api.delete(`/goals/${g.id}`);
      loadGoals();
      loadDash();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const createTheme = async () => {
    if (!newTheme.name) return;
    try {
      await api.post("/goal-themes", newTheme);
      toast.success("Eje creado");
      setNewTheme({ name: "", color: "#0ea5e9" });
      loadThemes();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo crear");
    }
  };

  const deleteTheme = async (t) => {
    if (!(await confirm({ title: "Eliminar eje", description: `¿Deseas eliminar el eje "${t.name}"? Esta acción no se puede deshacer.`, danger: true }))) return;
    try {
      await api.delete(`/goal-themes/${t.id}`);
      toast.success("Eje eliminado");
      loadThemes();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const updateMilestone = (i, patch) => {
    const arr = [...form.milestones];
    arr[i] = { ...arr[i], ...patch };
    setForm({ ...form, milestones: arr });
  };
  const addMilestone = () => setForm({
    ...form,
    milestones: [...form.milestones, { label: `Hito ${form.milestones.length + 1}`, target_date: null, target_value: 0, achieved: false, order_index: form.milestones.length }],
  });
  const removeMilestone = (i) => {
    const arr = [...form.milestones]; arr.splice(i, 1);
    setForm({ ...form, milestones: arr });
  };

  const yearsOptions = useMemo(() => {
    const cy = new Date().getFullYear();
    return [cy - 2, cy - 1, cy, cy + 1, cy + 2];
  }, []);

  return (
    <div data-testid="goals-page">
      <PageHeader
        title="Metas Corporativas — Asuntos Públicos"
        description="Planifica y controla los lineamientos sociales, educacionales, culturales y demás ejes de la compañía."
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setOpenThemes(true)} data-testid="manage-themes-btn">
              <Palette size={14} className="mr-1" /> Ejes
            </Button>
            <Button onClick={openCreate} data-testid="new-goal-btn">
              <Plus size={14} className="mr-1" /> Nueva meta
            </Button>
          </div>
        }
      />

      <div className="px-6 py-6">
        <Tabs value={tab} onValueChange={setTab}>
          <div className="flex items-center justify-between mb-4">
            <TabsList>
              <TabsTrigger value="dashboard" data-testid="tab-dashboard">Dashboard</TabsTrigger>
              <TabsTrigger value="lista" data-testid="tab-lista">Listado</TabsTrigger>
            </TabsList>
            <div className="flex items-center gap-2">
              <Label className="text-xs uppercase tracking-wider text-slate-500">Año</Label>
              <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
                <SelectTrigger className="w-28" data-testid="year-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {yearsOptions.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* DASHBOARD */}
          <TabsContent value="dashboard">
            {!dash ? (
              <div className="text-slate-500 py-10 text-center">Cargando…</div>
            ) : (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <KpiCard icon={Target} label="Metas año" value={dash.kpis.total} />
                  <KpiCard icon={CheckCircle} label="Logradas" value={dash.kpis.logradas} tone="emerald" />
                  <KpiCard icon={Spinner} label="En ejecución" value={dash.kpis.en_ejecucion} tone="amber" />
                  <KpiCard icon={Clock} label="Cumplimiento global" value={`${dash.kpis.overall_pct}%`} tone="blue" />
                </div>

                <div className="bg-white border border-slate-200 rounded-lg p-5">
                  <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-4">Semáforo</div>
                  <div className="flex items-center gap-6 text-sm">
                    <SemBadge tone="verde" v={dash.semaforo.verde} />
                    <SemBadge tone="amarillo" v={dash.semaforo.amarillo} />
                    <SemBadge tone="rojo" v={dash.semaforo.rojo} />
                  </div>
                </div>

                <div className="bg-white border border-slate-200 rounded-lg p-5">
                  <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-4">Cumplimiento por eje temático</div>
                  {dash.by_theme.length === 0 ? (
                    <div className="text-sm text-slate-400">Sin metas registradas para {year}.</div>
                  ) : (
                    <div className="space-y-3">
                      {dash.by_theme.map((t) => {
                        const pct = t.target > 0 ? Math.min(100, (t.current / t.target) * 100) : 0;
                        return (
                          <div key={t.name} data-testid={`theme-progress-${t.name}`}>
                            <div className="flex items-center justify-between text-sm mb-1">
                              <span className="font-medium text-slate-800 flex items-center gap-2">
                                <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: t.color }} />
                                {t.name}
                                <span className="text-xs text-slate-400">({t.count})</span>
                              </span>
                              <span className="text-xs text-slate-600 font-mono">
                                {t.current.toLocaleString("es-CL")} / {t.target.toLocaleString("es-CL")}
                                <span className="ml-2 font-semibold text-slate-900">{pct.toFixed(1)}%</span>
                              </span>
                            </div>
                            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                              <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: t.color }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}
          </TabsContent>

          {/* LISTADO */}
          <TabsContent value="lista">
            {/* Filters */}
            <div className="bg-white border border-slate-200 rounded-lg p-4 flex flex-wrap gap-3 items-end mb-4">
              <div className="min-w-[200px]">
                <Label className="text-xs uppercase tracking-wider text-slate-500">Eje</Label>
                <Select value={filterTheme || "__all__"} onValueChange={(v) => setFilterTheme(v === "__all__" ? "" : v)}>
                  <SelectTrigger data-testid="filter-theme"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todos</SelectItem>
                    {themes.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="min-w-[160px]">
                <Label className="text-xs uppercase tracking-wider text-slate-500">Estado</Label>
                <Select value={filterEstado || "__all__"} onValueChange={(v) => setFilterEstado(v === "__all__" ? "" : v)}>
                  <SelectTrigger data-testid="filter-estado"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todos</SelectItem>
                    {ESTADOS.map((e) => <SelectItem key={e.v} value={e.v}>{e.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {goals.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-lg p-10 text-center text-slate-500">
                Aún no hay metas registradas para {year}.{" "}
                <button className="text-slate-900 underline" onClick={openCreate}>Crea la primera</button>.
              </div>
            ) : (
              <div className="space-y-3">
                {goals.map((g) => {
                  const target = Number(g.target_value || 0);
                  const curr = Number(g.current_value || 0);
                  const pct = target > 0 ? Math.min(100, (curr / target) * 100) : 0;
                  return (
                    <div key={g.id} data-testid={`goal-row-${g.id}`} className="bg-white border border-slate-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <span
                          className="inline-block w-1.5 self-stretch rounded-sm"
                          style={{ background: g.theme_color || "#94a3b8" }}
                          aria-hidden
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                            <span className="font-display font-bold text-slate-900">{g.name}</span>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                              {g.theme_name || "Sin eje"}
                            </span>
                            <span className="text-xs text-slate-500">
                              {g.period_year_start === g.period_year_end ? g.period_year_start : `${g.period_year_start}–${g.period_year_end}`}
                            </span>
                            <Semaforo value={g.semaforo} />
                            <span className="text-xs text-slate-500">{ESTADOS.find((e) => e.v === g.estado)?.label || g.estado}</span>
                          </div>
                          {g.description && (
                            <div className="text-xs text-slate-500 mt-1 line-clamp-2">{g.description}</div>
                          )}
                          <div className="flex items-center justify-between mt-3 text-xs text-slate-700">
                            <span className="font-mono">
                              <strong className="text-slate-900">{curr.toLocaleString("es-CL")}</strong>
                              {" "}/ {target.toLocaleString("es-CL")} {g.unit}
                            </span>
                            <span className="font-semibold text-slate-900">{pct.toFixed(1)}%</span>
                          </div>
                          <div className="h-2 mt-1 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: g.theme_color || "#0ea5e9" }} />
                          </div>
                          <div className="flex items-center justify-between mt-2 text-xs text-slate-500">
                            <span>{g.responsable || "Sin responsable"} · modo: {g.progress_mode}</span>
                            <span>Actualizada {formatDate(g.updated_at)}</span>
                          </div>
                        </div>
                        <div className="flex flex-col gap-1 items-end">
                          <Button size="sm" variant="ghost" data-testid={`edit-goal-${g.id}`} onClick={() => openEdit(g)}>
                            <PencilSimple size={14} className="text-slate-600" />
                          </Button>
                          <Button size="sm" variant="ghost" data-testid={`delete-goal-${g.id}`} onClick={() => remove(g)}>
                            <Trash size={14} className="text-red-600" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Create / Edit goal dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="goal-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? "Editar meta" : "Nueva meta corporativa"}</DialogTitle>
            <DialogDescription>
              Define el lineamiento estratégico, su línea base, valor objetivo y cómo se calcula el avance.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Nombre de la meta *</Label>
                <Input
                  data-testid="goal-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div>
                <Label>Eje temático *</Label>
                <Select value={form.theme_id || undefined} onValueChange={(v) => setForm({ ...form, theme_id: v })}>
                  <SelectTrigger data-testid="goal-theme-select"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                  <SelectContent>
                    {themes.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Estado</Label>
                <Select value={form.estado} onValueChange={(v) => setForm({ ...form, estado: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ESTADOS.map((e) => <SelectItem key={e.v} value={e.v}>{e.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Año desde</Label>
                <Input type="number" value={form.period_year_start}
                  onChange={(e) => setForm({ ...form, period_year_start: Number(e.target.value) })} />
              </div>
              <div>
                <Label>Año hasta</Label>
                <Input type="number" value={form.period_year_end}
                  onChange={(e) => setForm({ ...form, period_year_end: Number(e.target.value) })} />
              </div>
              <div>
                <Label>Línea base</Label>
                <Input type="number" value={form.baseline}
                  onChange={(e) => setForm({ ...form, baseline: Number(e.target.value) })} />
              </div>
              <div>
                <Label>Valor objetivo *</Label>
                <Input type="number" value={form.target_value}
                  onChange={(e) => setForm({ ...form, target_value: Number(e.target.value) })} />
              </div>
              <div>
                <Label>Unidad</Label>
                <Input value={form.unit} placeholder="beneficiarios, proyectos, $MM…"
                  onChange={(e) => setForm({ ...form, unit: e.target.value })} />
              </div>
              <div>
                <Label>Responsable</Label>
                <Input value={form.responsable}
                  onChange={(e) => setForm({ ...form, responsable: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label>Descripción</Label>
                <Textarea rows={2} value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
            </div>

            {/* Cálculo del avance */}
            <div className="space-y-3 border-t border-slate-200 pt-4">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                Cálculo del avance
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label>Modo</Label>
                  <Select value={form.progress_mode} onValueChange={(v) => setForm({ ...form, progress_mode: v })}>
                    <SelectTrigger data-testid="progress-mode-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PROGRESS_MODES.map((m) => <SelectItem key={m.v} value={m.v}>{m.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {form.progress_mode !== "manual" && (
                  <>
                    <div>
                      <Label>Fuente</Label>
                      <Select value={form.auto_source || undefined} onValueChange={(v) => setForm({ ...form, auto_source: v, auto_metric: null })}>
                        <SelectTrigger><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                        <SelectContent>
                          {AUTO_SOURCES.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Métrica</Label>
                      <Select value={form.auto_metric || undefined} onValueChange={(v) => setForm({ ...form, auto_metric: v })}>
                        <SelectTrigger><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                        <SelectContent>
                          {(form.auto_source === "commitments" ? AUTO_METRICS_COMMITMENTS : AUTO_METRICS_PROJECTS).map((m) => (
                            <SelectItem key={m.v} value={m.v}>{m.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2 text-xs text-slate-500">
                      <strong>Nota:</strong> Para vincular proyectos/compromisos a esta meta, edítala luego de crearla y se agregará un selector dedicado.
                    </div>
                  </>
                )}
                {form.progress_mode !== "auto" && (
                  <div>
                    <Label>Avance actual</Label>
                    <Input type="number" value={form.current_value}
                      onChange={(e) => setForm({ ...form, current_value: Number(e.target.value) })}
                      data-testid="goal-current-input" />
                  </div>
                )}
              </div>
            </div>

            {/* Hitos */}
            <div className="space-y-2 border-t border-slate-200 pt-4">
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                  Hitos intermedios (opcional)
                </div>
                <Button size="sm" variant="outline" onClick={addMilestone} data-testid="add-milestone-btn">
                  <Plus size={12} className="mr-1" /> Hito
                </Button>
              </div>
              {form.milestones.length === 0 ? (
                <div className="text-xs text-slate-400">Sin hitos definidos.</div>
              ) : (
                <div className="space-y-2">
                  {form.milestones.map((m, i) => (
                    <div key={i} data-testid={`milestone-${i}`} className="grid grid-cols-12 gap-2 items-end">
                      <div className="col-span-4">
                        <Label className="text-[10px]">Etiqueta</Label>
                        <Input value={m.label} onChange={(e) => updateMilestone(i, { label: e.target.value })} />
                      </div>
                      <div className="col-span-3">
                        <Label className="text-[10px]">Fecha</Label>
                        <Input type="date" value={m.target_date || ""} onChange={(e) => updateMilestone(i, { target_date: e.target.value })} />
                      </div>
                      <div className="col-span-3">
                        <Label className="text-[10px]">Valor</Label>
                        <Input type="number" value={m.target_value || 0} onChange={(e) => updateMilestone(i, { target_value: Number(e.target.value) })} />
                      </div>
                      <div className="col-span-1 flex items-center pb-2">
                        <input type="checkbox" checked={m.achieved} onChange={(e) => updateMilestone(i, { achieved: e.target.checked })} title="Logrado" />
                      </div>
                      <div className="col-span-1 flex justify-end">
                        <Button size="sm" variant="ghost" onClick={() => removeMilestone(i)}>
                          <X size={14} className="text-red-600" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} data-testid="save-goal-btn" className="bg-slate-900 text-white">
              {editingId ? "Guardar cambios" : "Crear meta"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Themes management */}
      <Dialog open={openThemes} onOpenChange={setOpenThemes}>
        <DialogContent data-testid="themes-dialog">
          <DialogHeader>
            <DialogTitle>Ejes temáticos</DialogTitle>
            <DialogDescription>
              Personaliza las áreas estratégicas de tu plan corporativo.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {themes.map((t) => (
              <div key={t.id} className="flex items-center justify-between p-2 rounded border border-slate-200">
                <div className="flex items-center gap-2">
                  <span className="inline-block w-3 h-3 rounded-sm" style={{ background: t.color }} />
                  <span className="text-sm">{t.name}</span>
                  {t.is_default && <span className="text-[10px] uppercase tracking-wider text-slate-400">default</span>}
                </div>
                {!t.is_default && (
                  <Button size="sm" variant="ghost" onClick={() => deleteTheme(t)}>
                    <Trash size={12} className="text-red-600" />
                  </Button>
                )}
              </div>
            ))}
          </div>

          <div className="border-t border-slate-200 pt-3 space-y-2">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Crear nuevo</div>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Label>Nombre</Label>
                <Input
                  data-testid="new-theme-name"
                  value={newTheme.name}
                  onChange={(e) => setNewTheme({ ...newTheme, name: e.target.value })}
                  placeholder="Patrimonio histórico"
                />
              </div>
              <div>
                <Label>Color</Label>
                <div className="flex gap-1 flex-wrap max-w-[180px]">
                  {COLORS_PRESET.map((c) => (
                    <button key={c} type="button"
                      onClick={() => setNewTheme({ ...newTheme, color: c })}
                      className={`w-6 h-6 rounded ${newTheme.color === c ? "ring-2 ring-offset-1 ring-slate-900" : ""}`}
                      style={{ background: c }}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div className="flex justify-end">
              <Button size="sm" onClick={createTheme} data-testid="create-theme-btn">Agregar eje</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, tone = "slate" }) {
  const toneClasses = {
    slate: "bg-slate-900 text-white",
    emerald: "bg-brand-green-600 text-white",
    amber: "bg-amber-500 text-white",
    blue: "bg-sky-600 text-white",
  };
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${toneClasses[tone]}`}>
        <Icon size={18} weight="regular" />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">{label}</div>
        <div className="font-display text-2xl font-bold text-slate-900 leading-tight">{value}</div>
      </div>
    </div>
  );
}

function SemBadge({ tone, v }) {
  const colors = {
    verde: "bg-brand-green-500", amarillo: "bg-amber-500", rojo: "bg-red-500",
  };
  const labels = { verde: "En curso / OK", amarillo: "Atención", rojo: "Crítico" };
  return (
    <div className="flex items-center gap-2">
      <span className={`w-3 h-3 rounded-full ${colors[tone]}`} />
      <span className="text-sm">{labels[tone]}</span>
      <span className="font-display font-bold text-lg ml-1">{v}</span>
    </div>
  );
}
